import React, { useEffect, useState } from "react";
import ModuleListing from "./ModuleListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/ModuleSlice";
import AddModuleWrapper from "../Add/AddModuleWrapper";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import EditModuleWrapper from "../Edit/EditModuleWrapper";
import {
  useChangeStatusModuleByIdMutation,
  useDeleteModuleByIdMutation,
  useGetAllModuleDataQuery,
} from "src/services/ModuleService";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const ModuleListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.module);
  const [selectedModuleId, setSelectedModuleId] = useState<string>();
  const [deleteModule] = useDeleteModuleByIdMutation();
  const [changeStatusModule] = useChangeStatusModuleByIdMutation();
  const { data, isLoading, isFetching } = useGetAllModuleDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["moduleName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteModule = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Module ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteModule(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusModule = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Module ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusModule({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const moduleName = "MODULE";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "moduleName",
      headerName: "Module Name",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "order",
      headerName: "order",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedModuleId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusModule(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteModule(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      <div>
        <ModuleListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddModuleWrapper onClose={() => dispatch(setIsOpenAddDialog(false))} />
      ) : null}
      {isOpenEditDialog ? (
        <EditModuleWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedModuleId={selectedModuleId || ""}
        />
      ) : null}
    </>
  );
};

export default ModuleListingWrapper;
